<template>
  <div>
    <v-hover
      v-slot:default="{ hover }">
      <v-text-field
        :id="'draggableModuleTextField' + module.id"
        class="playlist-draggable-module-body-1 moduleNameTruncate"
        v-model.trim="module.name"
        :error-messages="moduleNameErrors"
        hide-details="auto"
        solo
        flat
        :disabled="isUpdating || restrictPlaylistAccess || showSlideout"
        :append-icon="module.moduleItems.length === 0 && hover ? 'mdi-delete' : ''"
        @input="$v.module.name.$touch()"
        @blur="updateModuleName"
        @keyup.enter="updateModuleName"
        @click:append="deleteEmptyModule">{{ module.name }}</v-text-field>
      </v-hover>
      <draggable v-model="module.moduleItems" v-bind="dragOptions" group="bulb" :disabled="isUpdating || restrictPlaylistAccess || showSlideout" @update="updateItemOrder" @add="itemAddedToModule" :data-moduleid="module.id">
        <div v-for="moduleItem in module.moduleItems" :key="moduleItem.id">
          <DraggableBulb v-if="moduleItem.type === 'bulb'" :moduleBulb="moduleItem" :thumbnail="false"></DraggableBulb>
          <DraggableResource v-else-if="moduleItem.type === 'attachment'" :resource="moduleItem" :moduleId="module.id"></DraggableResource>
          <DraggableLink v-else-if="moduleItem.type === 'link'" :moduleLink="moduleItem" :moduleId="module.id"></DraggableLink>
          <DraggableText v-else-if="moduleItem.type === 'text'" :moduleText="moduleItem" :moduleId="module.id"></DraggableText>
        </div>
      </draggable>
      <v-row
        v-if="passbackBulbs.length === 0 || moduleSelected.id !== module.id">
        <v-col
          cols="12"
          md="4">
          <v-btn
            class="mt-2 justify-start"
            small
            block
            text
            :color="playlist.default_color"
            :disabled="isUpdating || restrictPlaylistAccess || showSlideout"
            @click="addBulbsToModule(module)"
            aria-label="Add bulbs to module"
            >
              <v-icon left>mdi-plus-circle-outline</v-icon> Add Bulbs
          </v-btn>
        </v-col>
        <v-col
          cols="12"
          md="5"
          offset-md="1">
          <v-btn
            class="mt-2 justify-start"
            small
            block
            text
            :color="playlist.default_color"
            :disabled="isUpdating || restrictPlaylistAccess || showSlideout"
            @click="addAttachmentToModule(module)"
            aria-label="Add attachment to module">
            <v-icon left>mdi-file-link-outline</v-icon> Add Other
          </v-btn>
        </v-col>
      </v-row>
      <v-alert
        v-else
        class="mt-2"
        elevation="10"
        dense
        dark
        :color="playlist.default_color">{{passbackBulbs.length}} bulb{{passbackBulbs.length === 1 ? '' : 's'}} queued.
      </v-alert>
  </div>
</template>

<script>
import {
  mapState,
  mapMutations,
  mapGetters,
  mapActions,
} from 'vuex';
// Validations from Vuelidate
import {
  maxLength,
  required,
} from 'vuelidate/lib/validators';
import _ from 'lodash';
import draggable from 'vuedraggable';
import DraggableBulb from './DraggableBulb.vue';
import DraggableLink from './DraggableLink.vue';
import DraggableResource from './DraggableResource.vue';
import DraggableText from './DraggableText.vue';

export default {
  name: 'DraggableModule',
  props: {
    module: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  components: {
    draggable,
    DraggableBulb,
    DraggableLink,
    DraggableResource,
    DraggableText,
  },
  methods: {
    ...mapMutations([
      'setModuleSelected',
      'setSlideout',
    ]),
    ...mapActions([
      'addItemToModule',
      'deleteModule',
      'updateModule',
    ]),
    itemAddedToModule(e) {
      // This function is fired when a new bulb is added to a module
      const oldModuleId = _.get(e, 'from.dataset.moduleid', null) || 0;
      const addedItem = this.module.moduleItems[e.newIndex];
      let itemId;
      if (_.has(addedItem, 'itemId')) {
        // eslint-disable-next-line
        itemId = addedItem.itemId; // already in moduleItem table
      }
      if (_.has(addedItem, 'bulb.id')) {
        itemId = addedItem.bulb.id; // bulb assigned to section - not in playist
      }
      if (_.has(addedItem, 'resource.attachmentId')) {
        itemId = addedItem.resource.attachmentId; // attachment assigned to playlist - not in playist
      }
      const itemName = _.get(addedItem, 'resource.name', null);
      this.addItemToModule({
        moduleId: this.module.id,
        oldModuleId,
        oldItemIndex: e.oldIndex,
        item: {
          id: itemId,
          type: this.module.moduleItems[e.newIndex].type,
          order: e.newIndex,
          name: itemName,
        },
      });
    },
    addAttachmentToModule(module) {
      this.setModuleSelected(module); // Will this break adding bulbs flow?
      this.setSlideout({
        type: 'playlistAttachmentPicker',
      });
    },
    addBulbsToModule(module) {
      this.setModuleSelected(module);
      this.setSlideout({
        type: 'playlistBulbPicker',
      });
    },
    setModuleInputColor() {
      const input = document.getElementById(`draggableModuleTextField${this.module.id}`);
      if (input) input.style.color = this.playlist.default_color;
    },
    updateItemOrder() {
      // This function is fired if the bulbs in a module are rearranged
      const items = [];
      for (const moduleItem of this.module.moduleItems) {
        items.push({
          itemId: moduleItem.itemId,
          type: moduleItem.type,
          order: _.findIndex(this.module.moduleItems, mi => mi.itemId === moduleItem.itemId),
        });
      }
      this.updateModule({
        moduleId: this.module.id,
        module: {
          items,
        },
      });
    },
    deleteEmptyModule() {
      this.deleteModule(this.module.id);
    },
    updateModuleName(e) {
      if (!this.$v.module.name.$error && this.$v.module.name.$dirty) {
        this.$v.module.name.$reset();
        this.updateModule({
          moduleId: this.module.id,
          module: {
            name: e.target.value,
          },
        });
      }
    },
  },
  computed: {
    ...mapState([
      'isUpdating',
      'moduleSelected',
      'passbackBulbs',
      'playlist',
      'showSlideout',
      'usedBulbIds',
      'user',
    ]),
    ...mapGetters([
      'restrictPlaylistAccess',
    ]),
    dragOptions() {
      return {
        animation: 150,
        group: 'module',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
    moduleNameErrors() {
      const errors = [];
      if (!this.$v.module.name.$dirty) return errors;
      if (!this.$v.module.name.required) {
        errors.push('Module name is required.');
      }
      if (!this.$v.module.name.maxLength) {
        errors.push('Module name max length is 255 characters.');
      }
      return errors;
    },
  },
  mounted() {
    this.setModuleInputColor();
  },
  watch: {
    'playlist.default_color': function defaultColorWatcher(n) {
      if (n) this.setModuleInputColor();
    },
  },
  validations: {
    module: {
      name: {
        maxLength: maxLength(255),
        required,
      },
    },
  },
};
</script>

<style scoped>

  div .moduleNameTruncate >>> input {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .playlist-draggable-module-body-1 {
    font-size: 1rem !important;
    letter-spacing: .03125em !important;
    line-height: 1.5rem;
  }
</style>
